import { createTheme, type Theme } from '@mui/material/styles';
import { DISCRIMINATION_COLORS, LEGACY_TOKENS, type LegacyTokens } from './qualityPalette';

/**
 * Marketing-aligned theme, extracted 1:1 from the public v2.examfolio.com site
 * (WordPress + Divi). It re-skins the app to the official ExamFolio brand:
 * navy + amber on warm cream, with Plus Jakarta Sans / Inter.
 *
 * Verified against the Divi global tokens served on the landing page:
 *   --gcid-primary-color:   #1a2b4a  (navy)
 *   --gcid-secondary-color: #f59e0b  (amber)
 *   --gcid-link-color:      #2563eb  (link blue)
 *   --gcid-body-color:      #666666  / slate #64748b
 *   --et_global_heading_font: 'Plus Jakarta Sans'
 *   --et_global_body_font:    'Inter'
 * Warm surfaces sampled from the page: #faf8f2 (cream), #e0ddd4 / #e8e5dc borders,
 * #fef3c7 (amber tint), #ea580c (amber dark).
 *
 * Drop-in replacement for `createAppTheme` — identical signature, so
 * BrandingProvider and per-tenant primary/secondary overrides keep working.
 * The `discrimination` palette is kept verbatim (it encodes point-biserial
 * item-quality buckets, not branding). See [[qualityPalette]].
 */

/** Brand tokens lifted from the v2 marketing site. */
export const V2_BRAND = {
  navy: '#1a2b4a',
  navyDark: '#11203a',
  amber: '#f59e0b',
  amberDark: '#ea580c',
  amberTint: '#fef3c7',
  link: '#2563eb',
  slate: '#64748b',
  cream: '#faf8f2',
  warmBorder: '#e0ddd4',
  warmBorderSoft: '#e8e5dc',
} as const;

/**
 * Categorical palette for charts (pie slices, bars). Leads with the brand navy
 * and amber, then a distinct, accessible spread of hues that sit harmoniously
 * with the brand. Cycle with `i % length`.
 */
export const BRAND_CHART_COLORS = [
  V2_BRAND.navy, // #1a2b4a
  V2_BRAND.amber, // #f59e0b
  V2_BRAND.link, // #2563eb
  '#0f766e', // teal
  '#7c3aed', // violet
  '#dc2626', // red
  V2_BRAND.slate, // #64748b
  '#16a34a', // green
  '#db2777', // pink
  V2_BRAND.amberDark, // #ea580c
] as const;

/**
 * `palette.legacy` keeps its shape (consumers read `legacy.header`,
 * `legacy.navActive`, etc.) but the brand-facing tokens are remapped to the v2
 * palette. Exam-taking tokens (answered indicator, beige body) are left as the
 * legacy values so the candidate exam flow is untouched.
 */
const V2_LEGACY_TOKENS: LegacyTokens = {
  ...LEGACY_TOKENS,
  usedInExam: V2_BRAND.amber,
  hover: '#f4f1ea',
  header: V2_BRAND.navy,
  headerText: '#9aa6bd',
  navActive: V2_BRAND.amber,
  divider: V2_BRAND.warmBorder,
};

export function createV2BrandTheme(primary?: string | null, secondary?: string | null): Theme {
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: primary && primary.length > 0 ? primary : V2_BRAND.navy },
      secondary: { main: secondary && secondary.length > 0 ? secondary : V2_BRAND.amber },
      info: { main: V2_BRAND.link },
      warning: { main: V2_BRAND.amberDark },
      text: { primary: '#1f2937', secondary: V2_BRAND.slate },
      background: { default: V2_BRAND.cream, paper: '#ffffff' },
      divider: V2_BRAND.warmBorder,
      discrimination: DISCRIMINATION_COLORS,
      legacy: V2_LEGACY_TOKENS,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      // Headings use Plus Jakarta Sans, matching --et_global_heading_font.
      h1: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 700 },
      h2: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 700 },
      h3: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 700 },
      h4: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 600 },
      h5: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 600 },
      h6: { fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Workaround for mui/material-ui#13264, scoped to MULTILINE outlined
          // fields only — single-line fields never exhibit the bug and must
          // keep MUI's default notch behaviour untouched. On multiline fields
          // the label can shrink before MUI recalculates the notch, so the
          // outline border draws straight through the label text — a
          // "strikethrough" that only clears on the next interaction. Whenever
          // the label is shrunk we force the notch open: there is always a label
          // to fill it, so this is correct in every case and simply removes the
          // timing dependency. The `.MuiInputBase-multiline` segment limits this
          // to <textarea>-backed fields.
          '.MuiInputLabel-outlined.MuiInputLabel-shrink + .MuiInputBase-multiline .MuiOutlinedInput-notchedOutline > legend':
            {
              maxWidth: '100%',
            },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: V2_BRAND.warmBorder },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // The marketing CTAs are pill-ish; soften the corners site-wide.
          root: { borderRadius: 8 },
          // Primary call-to-action = amber with navy text, like the site's
          // "Learn more" button, so the main action pops against navy/cream.
          containedPrimary: {
            backgroundColor: V2_BRAND.amber,
            color: V2_BRAND.navy,
            '&:hover': { backgroundColor: '#d97706' },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          // Brand bar = navy, matching the site header.
          colorPrimary: { backgroundColor: V2_BRAND.navy },
        },
      },
    },
  });
}
